// 警報太多時合併（VMS UX 評審第四輪 R4-P1-2、B4-08；SPEC UX-14「同一台合併」、#vms-web-480）：
// 清單與鈴鐺依「同一台＋同一種」合成一列「× N 次（最早 HH:MM）」，可以一次處理。
// 只合併相鄰的（依時間排好的清單裡連在一起）、而且相鄰兩則相隔不超過 gap——中間隔了別的事件或停了一陣子就分開算。

/** 相鄰兩則相隔多久以內算同一波（2 分鐘：拆除 / 遮擋這類每十幾秒一則的洪水會合成一列，隔天的同一種不會）。 */
export const ALARM_MERGE_GAP_MS = 120_000;

export interface AlarmGroup<R> {
  head: R;
  /** 同一波裡比 head 舊的（head 是最新的一則） */
  rest: R[];
}

/**
 * rows 要已經依時間新到舊排好。key = null 的列不合併（非警報、書籤…）。
 * open(r) = 這則還沒處理（鈴鐺：還沒讀；清單：open / acknowledged）——還沒處理的「同一台＋同一種」不管隔多久、中間夾了什麼
 * 都併進同一列（第五輪 B5-10：同一個 CH3 拆除 3 分鐘內不再分成三列）；處理過的照「相鄰、相隔 gap 內」的舊規則。
 */
export function groupAlarms<R>(
  rows: R[],
  keyOf: (r: R) => string | null,
  tsOf: (r: R) => number,
  opts: { gap?: number; open?: (r: R) => boolean } = {},
): AlarmGroup<R>[] {
  const gap = opts.gap ?? ALARM_MERGE_GAP_MS;
  const out: AlarmGroup<R>[] = [];
  /** 還沒處理的群組：key → 群組 */
  const openBy = new Map<string, AlarmGroup<R>>();
  let lastKey: string | null = null;
  for (const r of rows) {
    const k = keyOf(r);
    const isOpen = !!opts.open && k !== null && opts.open(r);
    if (isOpen && openBy.has(k!)) {
      openBy.get(k!)!.rest.push(r);
      continue;
    }
    const g = out[out.length - 1];
    if (!isOpen && k !== null && g && lastKey === k && !(opts.open && opts.open(g.head))) {
      const prev = g.rest.length ? g.rest[g.rest.length - 1] : g.head;
      if (Math.abs(tsOf(prev) - tsOf(r)) <= gap) {
        g.rest.push(r);
        continue;
      }
    }
    const ng = { head: r, rest: [] as R[] };
    out.push(ng);
    if (isOpen) openBy.set(k!, ng);
    lastKey = k;
  }
  return out;
}

/** 排序用的欄位：時間（ms）、設備鍵（device_ref，通道 = <主機>:chN）、事件 key、event_id。 */
export interface AlarmSortKey {
  ts: number;
  ref: string;
  key: string;
  id: string;
}

/**
 * 警報 / 事件列的排序（SPEC #vms-web-290 第十一輪收尾 B11-05）：時間新到舊；同一秒（NVR 同時回報多路影像訊號中斷）
 * 再依設備鍵自然排序（CH4 在 CH10 前）、事件 key、event_id——總覽「還沒處理的警報」、警報分頁、全部事件共用，列順序才一樣。
 */
export function compareAlarm(a: AlarmSortKey, b: AlarmSortKey): number {
  return b.ts - a.ts || a.ref.localeCompare(b.ref, 'en', { numeric: true }) || a.key.localeCompare(b.key, 'en') || a.id.localeCompare(b.id, 'en');
}
